'use client'

// Views loop — the cycle demo. Spec:
// apps/frontend/design/legix-website-v2/Website.dc.html (section 5 · VIEWS
// LOOP). This component only renders the static shell (#lgxViews and the #cy*
// ids); the imperative engine in public/landing/views/cycle-embed.js finds the
// ids, injects the rail/card/grid content, and drives the loop itself once the
// page has loaded. React must never write into those nodes — it renders them
// empty once and the engine owns their children from then on.
//
// What React DOES own is when that engine may run. The effect below is the only
// React code here and it touches no DOM: mounting/unmounting this shell is the
// only signal the engine can get that its nodes exist or are gone.

import { useEffect } from 'react'

declare global {
  interface Window {
    // the handle cycle-embed.js publishes once it has been evaluated
    LGX_CYCLE?: { start: () => void; stop: () => void }
  }
}

export function LandingViewsLoop() {
  // FRONTEND-4: next/script evaluates cycle-embed.js once per SPA session and
  // the engine's interval, resize listener and pending timeouts survive a
  // client-side navigation away from the landing page. They then ran against a
  // destroyed section — `Cannot set properties of null (setting 'innerHTML')`
  // on #cyIcon — and kept repainting nothing on every other page of the app.
  // Unmount is the teardown signal; mount is the restart signal, because the
  // cached script tag will not re-execute when a visitor navigates back.
  useEffect(() => {
    // undefined on a first load: the script has not run yet and starts itself
    window.LGX_CYCLE?.start()
    return () => window.LGX_CYCLE?.stop()
  }, [])

  return (
    <div
      id="lgxViews"
      data-screen-label="Views"
      data-slot="landing-views"
      style={{ borderTop: '1px solid var(--line)', padding: '96px 0 88px', overflow: 'hidden' }}
    >
      <header id="cyHead">
        <h2 className="cy-title">Everything your reports show. And everything they never could.</h2>
        <p className="cy-sub">
          Statements, registers, and lists are all one thing: a view of the ledger. Change what it
          shows, how it’s grouped, how time runs. Every number opens.
        </p>
      </header>
      <div id="cyLayout" className="lgx-px">
        <div id="cyUnit">
          <div id="cyRail"></div>
          <div className="cy-stage">
            <div className="vs-card mt-card">
              <div className="vs-editor">
                <span className="vs-eicbox" id="cyIcon"></span>
                <span className="vs-ecol">
                  <input className="vs-ename mt-fade" id="cyName" readOnly />
                  <input className="vs-edesc mt-fade" id="cyDesc" readOnly />
                </span>
              </div>
              <div className="mt-chiprow" id="cyChips"></div>
            </div>
            <div className="mt-gridwrap">
              <div className="mt-gridlayer" id="cyGridA"></div>
              <div className="mt-gridlayer mt-gone" id="cyGridB"></div>
            </div>
          </div>
          <div id="cyPeekSlot">
            <div id="cyPeek"></div>
          </div>
        </div>
      </div>
      <div id="cyFeats">
        <div className="lgx-fcol">
          <svg
            className="lgx-fic"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 10h14l-4 -4 M17 14h-14l4 4"></path>
          </svg>
          <h3 className="lgx-ft">Start anywhere. Save anything.</h3>
          <p className="lgx-fd">
            Peek into a number, regroup, rescope. You’re never rebuilding a report, just pivoting
            the one in front of you.
          </p>
        </div>
        <div className="lgx-fcol">
          <svg
            className="lgx-fic"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 17l6 -5l-6 -5 M12 19h8"></path>
          </svg>
          <h3 className="lgx-ft">Built for your tools, not just your screen.</h3>
          <p className="lgx-fd">
            Every view is available to your tools over MCP. Anything you can see or do here, they
            can do too.
          </p>
        </div>
        <div className="lgx-fcol">
          <svg
            className="lgx-fic"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 15V3 M8 7l4 -4l4 4 M4 14v5a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-5"></path>
          </svg>
          <h3 className="lgx-ft">Exportable and shareable.</h3>
          <p className="lgx-fd">
            Share a view as a link and the numbers stay live. And when someone wants a CSV, any view
            exports clean.
          </p>
        </div>
      </div>
    </div>
  )
}
